import bidiFactory, { type BidiInstance } from 'bidi-js';
import type {
  StringParser,
  StringParserParseOptions,
  TextSegmentParser,
} from './types';
import { parseGrapheme, parseWord } from './parsers';

type Params = {
  locale: string;
  granularity: 'character' | 'word';
};

const scopeToGranularityMap = {
  character: 'grapheme',
  word: 'word',
} as const;

type TextSegmentGranularity =
  (typeof scopeToGranularityMap)[keyof typeof scopeToGranularityMap];

const granularityToParserMap: Record<
  TextSegmentGranularity,
  TextSegmentParser
> = {
  grapheme: parseGrapheme,
  word: parseWord,
} as const;

let bidi: BidiInstance | undefined;

export const createStringParser = (params: Params): StringParser => {
  const { locale, granularity: _granularity } = params;

  const granularity = scopeToGranularityMap[_granularity];

  if (!granularity) {
    throw new Error('StringParser: granularity is undefined!');
  }

  if (!bidi) {
    bidi = bidiFactory();
  }

  const bidiInstance = bidi;

  const segmenter = new Intl.Segmenter(locale, {
    granularity,
  });

  const defaultParseOptions: StringParserParseOptions = {
    textDirection: 'ltr',
  };

  return {
    parse: (str, options) => {
      const { textDirection } = options ?? defaultParseOptions;
      const parser = granularityToParserMap[granularity];
      return parser(str, textDirection, segmenter, bidiInstance);
    },
  };
};
